/** Indian-notation money and rate formatting. Pure functions, no DI. */

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const NUM = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return INR.format(value);
}

/** 16500000 -> "₹1.65 Cr". Used wherever space is tight. */
export function formatCompactCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)} K`;
  return `${sign}₹${NUM.format(abs)}`;
}

export function formatPsf(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `₹${NUM.format(Math.round(value))}/sq.ft`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return NUM.format(value);
}

export function formatPct(
  value: number | null | undefined,
  fractionDigits = 1,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(fractionDigits)}%`;
}

export function formatSignedPct(
  value: number | null | undefined,
  fractionDigits = 1,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(fractionDigits)}%`;
}

export function monthsSince(isoDate: string, now = new Date()): number {
  const then = new Date(isoDate);
  if (Number.isNaN(then.getTime())) return Number.POSITIVE_INFINITY;
  return Math.max(
    0,
    (now.getFullYear() - then.getFullYear()) * 12 +
      (now.getMonth() - then.getMonth()),
  );
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * True for a real number or a numeric string. Number-typed `<input>` controls
 * bound via `[type]="type()"` (a property binding rather than a static
 * `type="number"` attribute) don't match Angular's NumberValueAccessor at
 * compile time, so their form values round-trip as strings — this guard
 * tolerates that rather than silently dropping every typed value.
 */
export function isFiniteNumber(value: unknown): value is number {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string' && value.trim() !== '') return Number.isFinite(Number(value));
  return false;
}
